import type { JsonValue, MapDocument, PropBehaviour } from "../document/types.ts";
import { propPlace, type IssueEntry, type IssueNames, type PropPlace } from "./issue-entries.ts";
import type { ObjectTypeDefinition } from "./objects.ts";

/**
 * The Behaviours view (P1.6-T30, brief 6.3 item 1), the outliner's Things tab: every behaviour type the game
 * registers, in its order, each with the props of the map that carry it, then every type the map's props carry
 * that the game does not register (ADR-053 keeps such a behaviour as data), in the order first met. Each prop is
 * named, placed and framed as the Issues list names a prop (`propPlace`) and carries the Issues list's lines
 * that point at it, so the with-issues filter keeps exactly the props that list names. A prop of an
 * unregistered type also carries its saved data as rows, since no schema reads it. A thing also carries its own
 * label when its behaviour holds one (`thingLabel`), which the view shows before the frame's name, so two props
 * drawn from one frame read apart (ADR-070's amendment "the Behaviours view as built", line of 2026-10-12).
 */

/**
 * One saved value of a behaviour, as a label and its words.
 */
export interface ThingDatum {
  readonly label: string;
  readonly value: string;
}

/**
 * One prop carrying a behaviour: where it stands, named by its frame as an Issues line names it; its own label,
 * null when its behaviour holds none; the Issues list's lines pointing at it; and its saved data when its type
 * is not registered (null otherwise).
 */
export interface ThingEntry extends PropPlace {
  readonly label: string | null;
  readonly issues: readonly IssueEntry[];
  readonly data: readonly ThingDatum[] | null;
}

/**
 * The fields of a behaviour that name the thing, strongest first. A game says what a thing is called in a field
 * of one of these names (a sign's `text`, a chest's `name`); no other field is read as a label, so an id or a
 * lookup's key never names a thing.
 */
export const THING_LABEL_FIELDS: readonly string[] = ["label", "name", "title", "text"];

/**
 * The most characters a thing's label holds, so a row and its accessible name stay short however long a sign's
 * text is.
 */
export const THING_LABEL_MOST = 72;

const CUT_MARK = "...";
const WORD_REACH = 16;

/**
 * A line held to `THING_LABEL_MOST` characters: as it is when it fits, else cut and ended with three full
 * stops, at the end of a word when one ends within the last `WORD_REACH` characters of the room, else mid-word.
 *
 * @param line The line, its spaces already closed up.
 * @returns The line, at most `THING_LABEL_MOST` characters.
 */
function bounded(line: string): string {
  if (line.length <= THING_LABEL_MOST) {
    return line;
  }
  const room = THING_LABEL_MOST - CUT_MARK.length;
  const space = line.lastIndexOf(" ", room);
  return `${line.slice(0, space >= room - WORD_REACH ? space : room)}${CUT_MARK}`;
}

/**
 * A thing's own label: the first of `THING_LABEL_FIELDS` its behaviour holds as words, as one line (the first
 * line that holds words, its runs of spaces closed up) of at most `THING_LABEL_MOST` characters.
 *
 * @param behaviour The behaviour.
 * @returns The label, or null when no such field holds words.
 */
export function thingLabel(behaviour: PropBehaviour): string | null {
  for (const field of THING_LABEL_FIELDS) {
    const value = behaviour.fields[field];
    if (typeof value !== "string") {
      continue;
    }
    for (const line of value.split("\n")) {
      const words = line.replace(/\s+/g, " ").trim();
      if (words !== "") {
        return bounded(words);
      }
    }
  }
  return null;
}

/**
 * One type and the props carrying it. `label` is the registered label, or the type's id for a type the game
 * does not register, which is its only name; `presence` marks a type driven by presence.
 */
export interface ThingGroup {
  readonly type: string;
  readonly label: string;
  readonly registered: boolean;
  readonly presence: boolean;
  readonly things: readonly ThingEntry[];
}

/**
 * A value's words: a string as written, anything else as JSON.
 *
 * @param value The value.
 * @returns The words.
 */
function words(value: JsonValue): string {
  return typeof value === "string" ? value : JSON.stringify(value);
}

/**
 * A behaviour's saved data as rows: each field by its key, each further state's frame and each transition
 * animation's first frame by the frame's name, then its activation cells ("Default cells" when it names none).
 *
 * @param behaviour The behaviour.
 * @param names The lookups, for frame names.
 * @returns The rows.
 */
export function behaviourData(behaviour: PropBehaviour, names: Pick<IssueNames, "frameName">): readonly ThingDatum[] {
  const cells = behaviour.activation;
  return [
    ...Object.entries(behaviour.fields).map(([label, value]) => ({ label, value: words(value) })),
    ...Object.entries(behaviour.frames ?? {}).map(([state, frame]) => ({ label: `${state} state`, value: names.frameName(frame) })),
    ...Object.entries(behaviour.animations ?? {}).map(([name, frame]) => ({ label: `${name} animation`, value: names.frameName(frame) })),
    { label: "Activation", value: cells === undefined ? "Default cells" : `${cells.length} ${cells.length === 1 ? "cell" : "cells"}` },
  ];
}

/**
 * The Issues list's lines by the prop they point at.
 *
 * @param issues The lines.
 * @returns The lines of each prop, keyed by layer and prop id.
 */
function linesByProp(issues: readonly IssueEntry[]): ReadonlyMap<string, readonly IssueEntry[]> {
  const byProp = new Map<string, IssueEntry[]>();
  for (const line of issues) {
    if (line.target.kind !== "prop") {
      continue;
    }
    const key = `${line.target.layerId}\n${line.target.propId}`;
    const lines = byProp.get(key);
    if (lines === undefined) {
      byProp.set(key, [line]);
    } else {
      lines.push(line);
    }
  }
  return byProp;
}

/**
 * The map's behaviour props grouped by type: every registered type in the game's order, one no prop carries
 * holding no thing, then the unregistered types the map's props carry, in the order first met; within a group,
 * the props in document order.
 *
 * @param map The map.
 * @param types The registered object types.
 * @param issues The Issues list's lines, as the live check last gave them; each thing carries those pointing at it.
 * @param names The lookups the Issues list names things by.
 * @returns The groups.
 */
export function thingGroups(map: MapDocument, types: readonly ObjectTypeDefinition[], issues: readonly IssueEntry[], names: IssueNames): readonly ThingGroup[] {
  const lines = linesByProp(issues);
  const registered = new Set(types.map((t) => t.type));
  const byType = new Map<string, ThingEntry[]>(types.map((t) => [t.type, []]));
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      const behaviour = prop.behaviour;
      if (behaviour === undefined) {
        continue;
      }
      const thing: ThingEntry = { ...propPlace(prop, layer, names), label: thingLabel(behaviour), issues: lines.get(`${layer.id}\n${prop.id}`) ?? [], data: registered.has(behaviour.type) ? null : behaviourData(behaviour, names) };
      const group = byType.get(behaviour.type);
      if (group === undefined) {
        byType.set(behaviour.type, [thing]);
      } else {
        group.push(thing);
      }
    }
  }
  return [...byType].map(([type, things]) => {
    const definition = types.find((t) => t.type === type);
    return { type, label: definition?.label ?? type, registered: definition !== undefined, presence: definition?.presence !== undefined, things };
  });
}

/**
 * The with-issues filter: each group's things that an Issues list line points at, and only the groups keeping
 * one.
 *
 * @param groups The groups.
 * @returns The groups filtered.
 */
export function thingsWithIssues(groups: readonly ThingGroup[]): readonly ThingGroup[] {
  return groups.map((group) => ({ ...group, things: group.things.filter((thing) => thing.issues.length > 0) })).filter((group) => group.things.length > 0);
}

/**
 * Why Clear cannot run on a group: the names of the layers, each once, holding one of its things and refusing
 * edits (locked or hidden), since Clear is one command and a lock or an eye refuses a command whole.
 *
 * @param group The group.
 * @param editable Whether a layer takes edits.
 * @returns The layers' names, empty when Clear can run.
 */
export function clearRefusedBy(group: ThingGroup, editable: (layerId: string) => boolean): readonly string[] {
  const names: string[] = [];
  for (const thing of group.things) {
    if (!editable(thing.target.layerId) && !names.includes(thing.layer)) {
      names.push(thing.layer);
    }
  }
  return names;
}
